import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, authApi } from '../services/auth';
import { migrationApi } from '../services/api';

const MigrationCreate = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    category: '',
    url: '',
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [targetFormData, setTargetFormData] = useState({
    category: '',
    url: '',
    username: '',
    password: '',
  });
  const [checkDuplicateStatus, setCheckDuplicateStatus] = useState(null);
  const [startMigrationStatus, setStartMigrationStatus] = useState(null);
  const [duplicatedCustomers, setDuplicatedCustomers] = useState([]);

  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Cleanup polling interval on unmount
  // Automatically select all customers when results are updated
  useEffect(() => {
    if (results && results.customers && results.customers.length > 0) {
      setSelectedCustomers(results.customers);
    }
  }, [results]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const fetchCategories = async () => {
    try {
      const response = await migrationApi.getCategories();
      if (
        response.data &&
        response.data.response &&
        response.data.response.data
      ) {
        setCategories(response.data.response.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateSessionId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.category ||
      !formData.url ||
      !formData.username ||
      !formData.password
    ) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setRequestStatus(null);

    try {
      const requestPayload = {
        webhookUrl: null,
        sessionId: generateSessionId(),
        category: formData.category,
        data: {
          action: 'listing_account',
          link: formData.url,
          username: formData.username,
          password: formData.password,
        },
      };

      const response = await migrationApi.createMigrationRequest(
        requestPayload,
      );

      if (
        response.data &&
        response.data.response &&
        response.data.response.id
      ) {
        const newRequestId = response.data.response.id;
        setRequestId(newRequestId);
        setRequestStatus('PENDING');

        // Start polling for status updates
        startPolling(newRequestId);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error creating migration request:', err);
      setError(
        err.response?.data?.message ||
          'Failed to create migration request. Please try again.',
      );
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    const interval = setInterval(async () => {
      try {
        const response = await migrationApi.getMigrationRequest(id);

        if (response.data && response.data.response) {
          const request = response.data.response;
          setRequestStatus(request.status);

          if (request.status === 'FINISHED') {
            setResults(request.response);
            setLoading(false);
            clearInterval(interval);
            setPollingInterval(null);
          } else if (request.status === 'ERROR') {
            setError('Migration request failed. Please try again.');
            setLoading(false);
            clearInterval(interval);
            setPollingInterval(null);
          }
        }
      } catch (err) {
        console.error('Error polling request status:', err);
        setError('Failed to check request status. Please refresh the page.');
        setLoading(false);
        clearInterval(interval);
        setPollingInterval(null);
      }
    }, 2000); // Poll every 2 seconds

    setPollingInterval(interval);
  };

  const handleReset = () => {
    setFormData({
      category: '',
      url: '',
      username: '',
      password: '',
    });
    setTargetFormData({
      category: '',
      url: '',
      username: '',
      password: '',
    });
    setRequestId(null);
    setRequestStatus(null);
    setResults(null);
    setError(null);
    setLoading(false);

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };

  const handleNavigateToRequests = () => {
    navigate('/requests');
  };

  // Customer selection handlers
  const handleCustomerSelect = (customer, isSelected) => {
    if (isSelected) {
      setSelectedCustomers((prev) => [...prev, customer]);
    } else {
      setSelectedCustomers((prev) => prev.filter((c) => c.id !== customer.id));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedCustomers(results.customers || []);
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleCheckDuplicate = async () => {
    if (selectedCustomers.length === 0) {
      setError('Please select at least one customer for migration.');
      return;
    }

    if (!targetFormData.url) {
      setError('Please fill in the target URL to check for duplications.');
      return;
    }

    setLoading(true);
    setError(null);
    setCheckDuplicateStatus('PENDING');

    try {
      // Extract customer IDs from selected customers
      const customerIds = selectedCustomers
        .map(
          (customer) => customer.id || customer.mobile_id || customer.sourceId,
        )
        .filter((id) => id); // Filter out any undefined/null values

      if (customerIds.length === 0) {
        setError('No valid customer IDs found in selected customers.');
        setCheckDuplicateStatus(null);
        setLoading(false);
        return;
      }

      const response = await migrationApi.checkDuplication(
        customerIds,
        targetFormData.url,
      );

      if (response.data && response.data.response) {
        const { duplicatedCustomers, availableCustomers } =
          response.data.response;

        // Store duplicated customers for visual indicators
        setDuplicatedCustomers(duplicatedCustomers);

        if (duplicatedCustomers.length > 0) {
          // Show alert with duplicated customers
          const duplicatedList = duplicatedCustomers.join(', ');
          alert(
            `Duplication Check Results:\n\n` +
              `Duplicated customers (${duplicatedCustomers.length}): ${duplicatedList}\n\n` +
              `Available customers: ${availableCustomers.length}\n\n` +
              `These duplicated customers have already been migrated to the target system and cannot be selected for migration.`,
          );

          // Automatically deselect duplicated customers
          const filteredCustomers = selectedCustomers.filter((customer) => {
            const customerId =
              customer.id || customer.mobile_id || customer.sourceId;
            return !duplicatedCustomers.includes(customerId);
          });

          if (filteredCustomers.length !== selectedCustomers.length) {
            setSelectedCustomers(filteredCustomers);
            alert(
              `Automatically removed ${duplicatedCustomers.length} duplicated customers from selection.`,
            );
          }
        } else {
          alert(
            `No duplications found! All ${selectedCustomers.length} selected customers are available for migration.`,
          );
        }

        setCheckDuplicateStatus('COMPLETED');
      }
    } catch (err) {
      console.error('Error checking duplicates:', err);
      setError(
        err.response?.data?.message ||
          'Failed to check duplicates. Please try again.',
      );
      setCheckDuplicateStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStartMigration = async () => {
    if (selectedCustomers.length === 0) {
      setError('Please select at least one customer for migration.');
      return;
    }
    setLoading(true);
    setError(null);
    setStartMigrationStatus('PENDING');

    try {
      const migrationRequestData = {
        linkSource: formData.url,
        categorySource: formData.category,
        dataSource: {
          username: formData.username,
          password: formData.password,
          action: 'listing_account',
        },
        linkDes: targetFormData.url,
        categoryDes: targetFormData.category,
        dataDes: {
          username: targetFormData.username,
          password: targetFormData.password,
          action: 'migration',
        },
      };

      const migrationResponse = await migrationApi.createNewMigrationRequest(
        migrationRequestData,
      );

      let newMigrationRequestId = null;
      if (migrationResponse.data) {
        if (migrationResponse.data.data && migrationResponse.data.data.id) {
          newMigrationRequestId = migrationResponse.data.data.id;
        } else if (
          migrationResponse.data.response &&
          migrationResponse.data.response.id
        ) {
          newMigrationRequestId = migrationResponse.data.response.id;
        } else if (migrationResponse.data.id) {
          newMigrationRequestId = migrationResponse.data.id;
        }
      }

      if (newMigrationRequestId) {
        const items = selectedCustomers.map((customer, index) => ({
          migrationRequestId: newMigrationRequestId,
          data: customer,
          sourceId: customer.id || customer.mobile_id || `customer_${index}`,
          destinationId: null,
          status: 'PENDING',
        }));

        const chunkSize = 50;
        const chunks = [];
        for (let i = 0; i < items.length; i += chunkSize) {
          chunks.push(items.slice(i, i + chunkSize));
        }

        const bulkPromises = chunks.map((chunk) =>
          migrationApi.createMigrationRequestItemsBulk(chunk),
        );

        await Promise.all(bulkPromises);

        // Fetch the migration request details to use in startMigration
        const requestResponse = await migrationApi.getNewMigrationRequest(
          newMigrationRequestId,
        );
        let migrationRequest = null;
        if (requestResponse.data) {
          if (requestResponse.data.data) {
            migrationRequest = requestResponse.data.data;
          } else if (requestResponse.data.response) {
            migrationRequest = requestResponse.data.response;
          } else {
            migrationRequest = requestResponse.data;
          }
        }

        if (migrationRequest) {
          // Start the migration process
          const sessionId = generateSessionId();
          const migrationRequestWithSession = {
            ...migrationRequest,
            sessionId: sessionId,
          };
          const startResponse = await migrationApi.startMigrationProcess(
            newMigrationRequestId,
            migrationRequestWithSession,
          );

          if (startResponse.data) {
            alert(
              `Migration request created and started successfully! ${selectedCustomers.length} customers added to migration queue.`,
            );
          } else {
            throw new Error('Failed to start migration process');
          }
        } else {
          throw new Error('Failed to fetch migration request details');
        }

        setSelectedCustomers([]);
        setTargetFormData({
          category: '',
          url: '',
          username: '',
          password: '',
        });
        setFormData({
          category: '',
          url: '',
          username: '',
          password: '',
        });
        setRequestId(null);
        setRequestStatus(null);
        setResults(null);
        setCheckDuplicateStatus(null);
        setStartMigrationStatus(null);

        navigate(`/migration`);
      } else {
        throw new Error('Failed to create migration request');
      }
    } catch (err) {
      console.error('Error starting migration:', err);
      setError(
        err.response?.data?.message ||
          'Failed to start migration. Please try again.',
      );
      setStartMigrationStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTargetInputChange = (e) => {
    const { name, value } = e.target;
    setTargetFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNavigateToMigration = () => {
    navigate('/migration');
  };

  return (
    <div className="migration-container">
      <header className="app-header">
        <h1>autobot</h1>
        <nav className="nav-links">
          <button className="nav-link" onClick={handleNavigateToRequests}>
            Requests
          </button>
          <button className="nav-link active">Migration</button>
        </nav>
        <button className="btn-logout" onClick={handleLogout}>
          Logout
        </button>
      </header>

      <div className="content-container">
        <div className="migration-header">
          <div className="header-content">
            <h1>Create New Migration</h1>
            <p className="subtitle">
              Set up a new migration request for customer accounts
            </p>
          </div>
        </div>

        <div className="migration-content">
          <div className="create-migration-container">
            <div className="create-migration-header">
              <span
                className="back-icon"
                onClick={() => {
                  setFormData({
                    category: '',
                    url: '',
                    username: '',
                    password: '',
                  });
                  setError(null);
                  handleNavigateToMigration();
                }}
                title="Back to Migration Requests"
              >
                &larr;
              </span>

              <h3>Create New Migration</h3>
            </div>
            <div className="migration-step">
              <h4>Step 1: System Configuration</h4>
              <div className="form-cards-container">
                <div className="form-card">
                  <h5>Original Game</h5>
                  <form className="migration-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="category">Category*</label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.code}>
                            {category.code} - {category.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="url">URL*</label>
                      <input
                        type="url"
                        id="url"
                        name="url"
                        value={formData.url}
                        onChange={handleInputChange}
                        placeholder="Enter the system URL"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="username">Username*</label>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="Enter username"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password*</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter password"
                        required
                        disabled={loading}
                      />
                    </div>
                  </form>
                </div>

                <div className="form-card">
                  <h5>Destination Game</h5>
                  <form className="migration-form">
                    <div className="form-group">
                      <label htmlFor="target-category">Category*</label>
                      <select
                        id="target-category"
                        name="category"
                        value={targetFormData.category}
                        onChange={handleTargetInputChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.code}>
                            {category.code} - {category.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="target-url">URL*</label>
                      <input
                        type="url"
                        id="target-url"
                        name="url"
                        value={targetFormData.url}
                        onChange={handleTargetInputChange}
                        placeholder="Enter the system URL"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="target-username">Username*</label>
                      <input
                        type="text"
                        id="target-username"
                        name="username"
                        value={targetFormData.username}
                        onChange={handleTargetInputChange}
                        placeholder="Enter username"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="target-password">Password*</label>
                      <input
                        type="password"
                        id="target-password"
                        name="password"
                        value={targetFormData.password}
                        onChange={handleTargetInputChange}
                        placeholder="Enter password"
                        required
                        disabled={loading}
                      />
                    </div>
                  </form>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="migration-step">
              <h4>Step 2: Migration Process</h4>
              <div className="button-group step-by-step">
                <button
                  type="button"
                  className="btn-primary step-button"
                  onClick={handleSubmit}
                  disabled={
                    loading ||
                    !formData.category ||
                    !formData.url ||
                    !formData.username ||
                    !formData.password ||
                    results !== null
                  }
                >
                  <span className="step-number">1</span>
                  {loading && requestStatus === 'PENDING'
                    ? 'Processing...'
                    : 'Fetch Customers List'}
                </button>
                <button
                  type="button"
                  className="btn-primary step-button"
                  onClick={handleCheckDuplicate}
                  disabled={
                    loading ||
                    !results ||
                    selectedCustomers.length === 0 ||
                    !targetFormData.category ||
                    !targetFormData.url ||
                    !targetFormData.username ||
                    !targetFormData.password ||
                    checkDuplicateStatus === 'COMPLETED'
                  }
                >
                  <span className="step-number">2</span>
                  {loading && checkDuplicateStatus === 'PENDING'
                    ? 'Checking...'
                    : 'Check Duplicate'}
                </button>
                <button
                  type="button"
                  className="btn-primary step-button"
                  onClick={handleStartMigration}
                  disabled={
                    loading ||
                    !results ||
                    selectedCustomers.length === 0 ||
                    checkDuplicateStatus !== 'COMPLETED' ||
                    !targetFormData.category ||
                    !targetFormData.url ||
                    !targetFormData.username ||
                    !targetFormData.password
                  }
                >
                  <span className="step-number">3</span>
                  {loading && startMigrationStatus === 'PENDING'
                    ? 'Starting...'
                    : 'Start Migration'}
                </button>
              </div>

              {results ? (
                <div className="results-container">
                  <div className="results-header">
                    <h5>Customer List</h5>
                    <p className="results-summary">
                      Found {results.customers?.length || 0} customer accounts
                    </p>
                  </div>

                  {results.customers && results.customers.length > 0 && (
                    <>
                      <div className="customer-selection-controls">
                        <div className="selection-info">
                          <label className="select-all-container">
                            <input
                              type="checkbox"
                              checked={
                                selectedCustomers.length ===
                                results.customers.length
                              }
                              onChange={(e) =>
                                handleSelectAll(e.target.checked)
                              }
                              disabled={checkDuplicateStatus !== 'COMPLETED'}
                            />
                            Select All ({selectedCustomers.length} of{' '}
                            {results.customers.length} selected)
                          </label>
                        </div>
                      </div>

                      <div className="customers-table-container">
                        <table className="customers-table">
                          <thead>
                            <tr>
                              <th>Select</th>
                              <th>ID</th>
                              <th>Mobile ID</th>
                              <th>Full Name</th>
                              <th>Gender</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.customers.map((customer, index) => {
                              const customerId =
                                customer.id || customer.mobile_id;
                              const isDuplicated =
                                duplicatedCustomers.includes(customerId);

                              return (
                                <tr
                                  key={index}
                                  className={
                                    isDuplicated ? 'duplicated-row' : ''
                                  }
                                  style={{
                                    backgroundColor: isDuplicated
                                      ? '#fee2e2'
                                      : 'transparent',
                                    opacity: isDuplicated ? 0.7 : 1,
                                  }}
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedCustomers.some(
                                        (c) => c.id === customer.id,
                                      )}
                                      onChange={(e) =>
                                        handleCustomerSelect(
                                          customer,
                                          e.target.checked,
                                        )
                                      }
                                      disabled={
                                        checkDuplicateStatus !== 'COMPLETED' ||
                                        isDuplicated
                                      }
                                    />
                                  </td>
                                  <td>{customer.id}</td>
                                  <td>{customer.mobile_id}</td>
                                  <td>{customer.full_name}</td>
                                  <td>{customer.gender}</td>
                                  <td>
                                    {isDuplicated ? (
                                      <span className="status-badge error">
                                        DUPLICATED
                                      </span>
                                    ) : (
                                      <span className="status-badge pending">
                                        AVAILABLE
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {(!results.customers || results.customers.length === 0) && (
                    <div className="no-results">
                      <p>No customer accounts found.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-results">
                  <p>Customer list will be displayed after fetching.</p>
                </div>
              )}
            </div>

            {requestId && (
              <div className="migration-step">
                <div className="status-container">
                  <div className="status-header">
                    <h4>Processing Status</h4>
                    <div className="request-info">
                      <span className="request-id">
                        Request ID: {requestId}
                      </span>
                      <span
                        className={`status-badge ${requestStatus?.toLowerCase()}`}
                      >
                        {requestStatus}
                      </span>
                    </div>
                  </div>

                  {loading && requestStatus === 'PENDING' && (
                    <div className="loading-indicator">
                      <div className="spinner"></div>
                      <p>
                        Processing migration request... This may take a few
                        minutes.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrationCreate;
